import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Fade, Tooltip, alpha, useMediaQuery, useTheme } from '@mui/material'
import { CellTower, ChatOutlined, DescriptionOutlined, PeopleAltOutlined, Schedule, SystemUpdateAltRounded } from '@mui/icons-material'
import dayjs from 'dayjs'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useChatUnread from '../hooks/useChatUnread'
import useUnreadDocs from '../hooks/useUnreadDocs'
import socket from '../utils/socket'
import { ROLES } from '../config/roles'
import { FONT_MONO } from '../config/fonts'

const REFRESH_MS = 60 * 1000     // safety net; presence events refresh sooner
const ROTATE_MS = 4000           // medium screens: one pill at a time
const STATUS_MS = 12000          // how long the live status stays before a banner
const BANNER_MS = 7000           // how long each banner stays
const UPDATE_DAYS = 7            // an application uploaded this recently is announced as an update
const UPDATE_REFRESH_MS = 5 * 60 * 1000

// "today", "yesterday", "3 days ago"
const daysAgo = (date) => {
  const days = dayjs().startOf('day').diff(dayjs(date).startOf('day'), 'day')
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`
}

// App-wide banners shown between the status views, in this order (each page's own banner is in its header). Any image works (SVG/PNG/JPG):
// they are shown 44px high and cropped on the right when the navbar is narrow.
const BANNERS = [
  { src: '/assets/banners/live-radar.svg', alt: 'Live radar: tracks from every outpost on one live map' },
  { src: '/assets/banners/replay-review.svg', alt: 'Replay & review: step back through recorded flights' },
  { src: '/assets/banners/unit-chat.svg', alt: 'Unit chat: messages, files and calls between units' },
  { src: '/assets/banners/night-ops.svg', alt: 'Around the clock: every outpost connected, day and night' },
]

// A Guest only has the chat, so they only see the chat banners
const CHAT_BANNERS = [
  { src: '/assets/banners/unit-chat.svg', alt: 'Unit chat: messages, files and calls between units' },
  { src: '/assets/banners/direct-messages.svg', alt: 'Direct messages: one-to-one chats, read receipts and file sharing' },
  { src: '/assets/banners/voice-video-calls.svg', alt: 'Voice & video calls: call anyone online straight from the chat' },
]

const utcOffset = (t) => {
  const min = t.utcOffset()
  const h = Math.floor(Math.abs(min) / 60)
  const m = Math.abs(min) % 60
  return `UTC${min >= 0 ? '+' : '-'}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`
}

const countOutposts = (nodes) => {
  let total = 0
  let online = 0
  const walk = (list) => (Array.isArray(list) ? list : [list]).forEach((n) => {
    if (!n) return
    if (n.type === 3) { total++; if (n.isOnline === true) online++ }
    walk(n.children || [])
  })
  walk(nodes)
  return { total, online }
}

// Middle of the navbar. Two kinds of slides take turns, with a crossfade:
//  - live status: outposts online, users online, unread chats, clock. If every pill fits in the
//    space it has, they are all shown; otherwise they rotate one at a time. The space is measured
//    (not the window width: the sidebar takes part of it). Clicking a pill opens its page.
//  - a banner image (BANNERS), one at a time.
// Order: status, banner 1, status, banner 2, ... Hovering pauses it.
const NavbarStatusStrip = ({ auth }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const axiosPrivate = useAxiosPrivate()
  const unread = useChatUnread(Boolean(auth?.accessToken))
  const newDocs = useUnreadDocs(Boolean(auth?.accessToken))   // readable documents I have not opened yet

  const showOne = useMediaQuery('(min-width:960px)')   // phones: no strip at all
  const boxRef = useRef(null)
  const measureRef = useRef(null)
  const [showAll, setShowAll] = useState(false)

  const accent = theme.palette.secondary.main
  const isDark = theme.palette.mode === 'dark'
  const isRoot = Boolean(auth?.roles?.includes(ROLES.Root))
  // Outposts are only for Root / Admin / User: a Guest has no access to /api/units
  const canSeeUnits = Boolean(auth?.roles?.some((r) => [ROLES.Root, ROLES.Admin, ROLES.User].includes(r)))
  const baseBanners = canSeeUnits ? BANNERS : CHAT_BANNERS

  const [outposts, setOutposts] = useState(null)   // { total, online } | null = unknown
  const [users, setUsers] = useState(null)
  const [now, setNow] = useState(() => dayjs())
  const [index, setIndex] = useState(0)
  const [slide, setSlide] = useState(0)          // even: status, odd: banner
  const [paused, setPaused] = useState(false)    // mouse over the strip
  const [updates, setUpdates] = useState([])     // applications uploaded in the last UPDATE_DAYS
  const announcedRef = useRef(false)             // jump to the update banner once per session

  // ---------- new application versions ----------
  useEffect(() => {
    if (!auth?._id || !canSeeUnits) return   // the Applications page is for Root / Admin / User
    let alive = true
    const load = async () => {
      try {
        const [appsRes, typesRes] = await Promise.all([
          axiosPrivate.get('/api/applications'),
          axiosPrivate.get('/api/appTypes'),
        ])
        const typeName = new Map((typesRes.data || []).map((t) => [String(t._id), t.name]))
        const since = dayjs().subtract(UPDATE_DAYS, 'day')
        // newest active upload per application type
        const latest = new Map()
        ;(appsRes.data || [])
          .filter((a) => a.active && a.fileUploadedAt && dayjs(a.fileUploadedAt).isAfter(since))
          .forEach((a) => {
            const key = String(a.appType)
            const cur = latest.get(key)
            if (!cur || dayjs(a.fileUploadedAt).isAfter(cur.uploadedAt)) {
              latest.set(key, { id: a._id, name: typeName.get(key) || a.originalFileName, version: a.version, uploadedAt: a.fileUploadedAt })
            }
          })
        const list = [...latest.values()].sort((x, y) => dayjs(y.uploadedAt).valueOf() - dayjs(x.uploadedAt).valueOf())
        if (alive) setUpdates(list)
      } catch {
        // not critical: the strip simply shows no update banner
      }
    }
    load()
    const t = setInterval(load, UPDATE_REFRESH_MS)
    return () => { alive = false; clearInterval(t) }
  }, [auth?._id, canSeeUnits, axiosPrivate])

  // The update banner goes first in the banner rotation
  const banners = useMemo(
    () => (updates.length ? [{ kind: 'update', src: 'update' }, ...baseBanners] : baseBanners),
    [updates.length, baseBanners]
  )

  // First time updates show up: show their banner right away instead of waiting a full cycle
  useEffect(() => {
    if (updates.length && !announcedRef.current) {
      announcedRef.current = true
      setSlide(1)
    }
  }, [updates.length])

  // ---------- data ----------
  const loadRef = useRef(null)
  loadRef.current = async () => {
    if (!auth?._id) return
    const [unitsRes, usersRes] = await Promise.allSettled([
      canSeeUnits
        ? axiosPrivate.get('/api/units/selectable', { params: { userId: auth._id } })
        : Promise.reject(new Error('no access to units')),
      axiosPrivate.get('/api/chatUsers/users', { params: { currentUserId: auth._id } }),
    ])
    if (unitsRes.status === 'fulfilled') {
      const c = countOutposts(unitsRes.value.data)
      setOutposts(c.total > 0 ? c : null)
    }
    if (usersRes.status === 'fulfilled' && Array.isArray(usersRes.value.data)) {
      const people = usersRes.value.data.filter((u) => !u.isBot)
      // Other users only (the list leaves me out): same numbers as the Chat page header
      setUsers({ total: people.length, online: people.filter((u) => u.isOnline).length })
    }
  }

  useEffect(() => {
    if (!auth?._id) return
    let timer = null
    const load = () => loadRef.current?.().catch(() => {})
    load()

    // Presence changes often come in bursts: reload once, shortly after the last one
    const onPresence = () => { clearTimeout(timer); timer = setTimeout(load, 1000) }
    socket.on('presenceUpdate', onPresence)
    const interval = setInterval(load, REFRESH_MS)

    return () => {
      socket.off('presenceUpdate', onPresence)
      clearInterval(interval)
      clearTimeout(timer)
    }
  }, [auth?._id])

  // ---------- clock ----------
  useEffect(() => {
    const t = setInterval(() => setNow(dayjs()), 1000)
    return () => clearInterval(t)
  }, [])

  // ---------- pills ----------
  const pills = []
  if (outposts) {
    pills.push({
      key: 'outposts',
      icon: CellTower,
      value: `${outposts.online}/${outposts.total}`,
      label: 'outposts online',
      tone: outposts.online === outposts.total ? 'good' : outposts.online === 0 ? 'bad' : 'warn',
      tip: 'Open the radar',
      to: '/radar',
    })
  }
  if (users) {
    pills.push({
      key: 'users',
      icon: PeopleAltOutlined,
      value: users.online,
      label: users.online === 1 ? 'user online' : 'users online',
      tone: 'neutral',
      tip: isRoot ? 'Open users' : 'Open chat',
      to: isRoot ? '/user' : '/chat',
    })
  }
  pills.push({
    key: 'unread',
    icon: ChatOutlined,
    value: unread.mentioned ? `@${unread.total}` : unread.total,
    label: unread.total === 1 ? 'unread chat' : 'unread chats',
    tone: unread.mentioned ? 'bad' : unread.total > 0 ? 'accent' : 'neutral',
    tip: 'Open chat',
    to: '/chat',
  })
  pills.push({
    key: 'docs',
    icon: DescriptionOutlined,
    value: newDocs,
    label: newDocs === 1 ? 'new document' : 'new documents',
    tone: newDocs > 0 ? 'accent' : 'neutral',
    tip: 'Open chat',
    to: '/chat',
  })
  pills.push({
    key: 'clock',
    icon: Schedule,
    value: now.format('HH:mm:ss'),
    label: `${now.format('ddd D MMM')} · ${utcOffset(now)}`,
    tone: 'neutral',
    mono: true,
  })

  const onBanner = banners.length > 0 && slide % 2 === 1
  const banner = onBanner ? banners[((slide - 1) / 2) % banners.length] : null

  // rotate the pills on medium screens (only while the status is showing)
  useEffect(() => {
    if (showAll || !showOne || onBanner) return
    const t = setInterval(() => setIndex((i) => i + 1), ROTATE_MS)
    return () => clearInterval(t)
  }, [showAll, showOne, onBanner])

  // status ↔ banner
  useEffect(() => {
    if (!showOne || paused || banners.length === 0) return
    const t = setTimeout(() => setSlide((n) => n + 1), onBanner ? BANNER_MS : STATUS_MS)
    return () => clearTimeout(t)
  }, [slide, onBanner, paused, showOne, banners.length])

  // load the banners once so the first fade-in isn't blank
  useEffect(() => {
    banners.forEach(({ src, kind }) => { if (kind) return; const img = new Image(); img.src = src })
  }, [banners])

  // Do all pills fit? Compare the hidden full row with the space the strip has
  useLayoutEffect(() => {
    const box = boxRef.current
    const row = measureRef.current
    if (!box || !row) return
    const check = () => setShowAll(row.scrollWidth <= box.clientWidth)
    check()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null
    ro?.observe(box)
    ro?.observe(row)
    return () => ro?.disconnect()
  }, [showOne, auth])

  if (!auth || !showOne) return null

  const toneColor = {
    good: isDark ? '#43a047' : '#2e7d32',
    warn: theme.palette.warning.main,
    bad: theme.palette.error.main,
    accent,
    neutral: theme.palette.text.secondary,
  }

  const renderPill = (p) => {
    const Icon = p.icon
    const color = toneColor[p.tone]
    const clickable = Boolean(p.to)

    const pill = (
      <Box
        key={p.key}
        // a div, not a <button>: index.css styles every "nav button" (white, with a margin)
        role={clickable ? 'link' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => navigate(p.to) : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter') navigate(p.to) } : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          height: 30,
          px: 1.25,
          borderRadius: '999px',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          cursor: clickable ? 'pointer' : 'default',
          fontSize: '0.8rem',
          color: theme.palette.text.secondary,
          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          border: `1px solid ${alpha(color, p.tone === 'neutral' ? 0.25 : 0.45)}`,
          transition: 'background-color .15s ease, border-color .15s ease',
          ...(clickable && {
            '&:hover, &:focus-visible': {
              outline: 'none',
              backgroundColor: alpha(accent, 0.1),
              borderColor: alpha(accent, 0.6),
            },
          }),
        }}
      >
        {/* status dot for outposts, icon for the rest */}
        {p.key === 'outposts' ? (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              boxShadow: `0 0 0 0 ${alpha(color, 0.6)}`,
              animation: p.tone === 'good' ? 'stripPulse 2s ease-out infinite' : 'none',
              '@keyframes stripPulse': {
                '0%': { boxShadow: `0 0 0 0 ${alpha(color, 0.6)}` },
                '70%': { boxShadow: `0 0 0 6px ${alpha(color, 0)}` },
                '100%': { boxShadow: `0 0 0 0 ${alpha(color, 0)}` },
              },
            }}
          />
        ) : (
          <Icon sx={{ fontSize: 16, color }} />
        )}
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            color: p.tone === 'neutral' ? theme.palette.text.primary : color,
            fontFamily: p.mono ? FONT_MONO : undefined,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {p.value}
        </Box>
        <span>{p.label}</span>
      </Box>
    )

    return p.tip ? <Tooltip key={p.key} title={p.tip} arrow>{pill}</Tooltip> : pill
  }

  const current = pills[index % pills.length]

  return (
    <Box
      ref={boxRef}
      aria-label="Status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: 'relative',
        flex: '1 1 0',
        minWidth: 0,
        mx: 2,
        display: 'flex',
        alignItems: 'center',
        // 'safe': if something is still too wide it is cut on the right only, never on both sides
        justifyContent: 'safe center',
        gap: 1,
        overflow: 'hidden',
      }}
    >
      {/* Invisible full row, only used to measure how wide all pills are together */}
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: 1, whiteSpace: 'nowrap', left: 0, top: 0 }}
      >
        {pills.map((p) => <Box key={p.key}>{renderPill({ ...p, tip: null, to: null })}</Box>)}
      </Box>

      {onBanner && banner.kind === 'update' ? (
        <Fade in key="banner-update" timeout={600}>
          <Box
            role="link"
            tabIndex={0}
            onClick={() => navigate('/application')}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate('/application') }}
            title="Open Applications to download the new version"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              height: 44,
              width: 520,
              maxWidth: '100%',
              pl: 0.75,
              pr: 1,
              borderRadius: '10px',
              cursor: 'pointer',
              overflow: 'hidden',
              userSelect: 'none',
              outline: 'none',
              background: 'linear-gradient(90deg, #0b2448 0%, #0f2d57 60%, #0b2448 100%)',
              border: `1px solid ${alpha(accent, 0.55)}`,
              boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.15)',
              transition: 'border-color .15s ease, box-shadow .15s ease',
              '&:hover, &:focus-visible': { borderColor: accent, boxShadow: `0 0 0 3px ${alpha(accent, 0.18)}` },
            }}
          >
            {/* icon with a soft pulse */}
            <Box
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                borderRadius: '8px',
                display: 'grid',
                placeItems: 'center',
                color: '#0b2448',
                backgroundColor: accent,
                animation: 'updatePulse 2s ease-out infinite',
                '@keyframes updatePulse': {
                  '0%': { boxShadow: `0 0 0 0 ${alpha(accent, 0.55)}` },
                  '70%': { boxShadow: `0 0 0 8px ${alpha(accent, 0)}` },
                  '100%': { boxShadow: `0 0 0 0 ${alpha(accent, 0)}` },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              <SystemUpdateAltRounded sx={{ fontSize: 18 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
              <Box sx={{ fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.14em', color: accent }}>
                {updates.length > 1 ? `${updates.length} NEW VERSIONS AVAILABLE` : 'NEW VERSION AVAILABLE'}
              </Box>
              <Box sx={{ fontSize: '0.82rem', color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{updates[0].name}</Box>
                {updates[0].version ? ` v${String(updates[0].version).replace(/^v/i, '')}` : ''}
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                  {` · uploaded ${daysAgo(updates[0].uploadedAt)}`}
                  {updates.length > 1 ? ` · +${updates.length - 1} more` : ''}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: 1.25,
                py: 0.5,
                borderRadius: '999px',
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#0b2448',
                backgroundColor: accent,
              }}
            >
              Update now
            </Box>
          </Box>
        </Fade>
      ) : onBanner ? (
        <Fade in key={`banner-${banner.src}`} timeout={600}>
          <Box
            component="img"
            src={banner.src}
            alt={banner.alt}
            title={banner.alt}
            draggable={false}
            sx={{
              display: 'block',
              height: 44,
              width: 520,
              maxWidth: '100%',
              objectFit: 'cover',
              objectPosition: 'left center',   // narrow navbar: keep the art and the title, crop the right
              borderRadius: '10px',
              border: `1px solid ${alpha(accent, 0.25)}`,
              boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.15)',
              userSelect: 'none',
            }}
          />
        </Fade>
      ) : showAll ? (
        <Fade in key={`status-${slide}`} timeout={600}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{pills.map(renderPill)}</Box>
        </Fade>
      ) : (
        <Fade in key={`${slide}-${current.key}`} timeout={500}>
          <Box>{renderPill(current)}</Box>
        </Fade>
      )}
    </Box>
  )
}

export default NavbarStatusStrip
